import * as React from 'react';
import styled from 'styled-components';
import {GrowplaceProgram, ProgramType} from 'api/course';
import {v4 as uuid} from 'uuid';
import {DragDropContext, Droppable, DropResult} from 'react-beautiful-dnd';
import AddButton from './components/AddButton';
import RenderChapters from './components/RenderChapters';
import CourseBuilderContext from 'components/CourseBuilder/CourseBuilderContext';
import {useGroupWeek} from 'hooks/useGroupWeek';
import {useTotalToggle} from 'hooks/useTotalToggle';
import reorder from 'helpers/reorder';

export const CHAPTER_TYPE = 'CHAPTER';
export const CHAPTER_CONTENT = 'CHAPTER_CONTENT';

const Header = styled.div`
  margin-bottom: 24px;
  column-gap: 16px;
  align-content: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

/**
 * The component render growplace content drag drop
 * @constructor
 */
export default function Growplace(): React.ReactElement {
  const {
    course,
    setCourse,
  } = React.useContext(CourseBuilderContext);

  const groupWeek = useGroupWeek();
  const totalToggle = useTotalToggle();

  if (!course) return <React.Fragment />;

  const program = course.programs || {
    type: ProgramType.Growplace,
    content: {
      chapters: [],
    },
  };
  const content = program?.content as GrowplaceProgram || {
    chapters: [],
  };

  /**
   * Drop event handler.
   * @param {DropResult} result
   */
  function handleDragEnd(result: DropResult): void {
    if (!result.destination) return;

    const from = result.source;
    const to = result.destination;

    switch (result.type) {
      case CHAPTER_TYPE: {
        (program && setCourse) && setCourse({
          ...course,
          programs: {
            ...program,
            content: {
              chapters: (
                reorder(content.chapters, from.index, to.index)
              ),
            },
          },
        });
        break;
      }
      case CHAPTER_CONTENT: {
        console.log(result);

        const fromChapter = content.chapters.find((chapter) => (
          chapter.id === from.droppableId
        ));

        const toChapter = content.chapters.find((chapter) => (
          chapter.id === to.droppableId
        ));

        const chapterContent = fromChapter?.content && (
          fromChapter.content[from.index]
        );

        if (!fromChapter || !toChapter || !chapterContent) return;

        fromChapter.content?.splice(from.index, 1);
        toChapter.content?.splice(to.index, 0, chapterContent);

        (program && setCourse) && setCourse({
          ...course,
          programs: {
            ...program,
            content: {
              chapters: content.chapters,
            },
          },
        });
        break;
      }
    }
  }

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        {groupWeek.renderControl()}
        {totalToggle.renderControl()}
      </Header>
    );
  }

  /**
   * This function will render chapters droppable.
   * @return {JSX.Element}
   */
  function renderChapters(): JSX.Element {
    return (
      <Droppable droppableId="chapters" type={CHAPTER_TYPE}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {totalToggle.wrapContent(
              <RenderChapters
                chapters={content.chapters}
                wrapContent={groupWeek.wrapContent}
              />
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  }

  /**
   * This function will render add button.
   * @return {JSX.Element}
   */
  function renderAddButton(): JSX.Element {
    /**
     * This creation handler.
     * @param {number} count
     * @return {void}
     */
    function handleCreate(count: number): void {
      for (let i = 0; i < count; i++) {
        content?.chapters.push({
          id: uuid(),
          content: [],
        });
      }

      setCourse && setCourse({
        ...course,
        programs: {
          ...program,
          content,
        },
      });
    }

    return (
      <AddButton
        onCreate={handleCreate}
      />
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      {renderHeader()}
      {renderChapters()}
      {renderAddButton()}
    </DragDropContext>
  );
}
